<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import V2ItemComandaSplitPreview from '@/components/modals/item/printing/V2ItemComandaSplitPreview.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import {
    groupsIdentical,
    printsApart,
    splitFromSwitches,
    stationList,
  } from '@/domain/catalog/printing-form';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2CatalogStationChips from '../shared/V2CatalogStationChips.vue';

  /**
   * «Impresión» of the product (plan §5.12): the category's stations or its
   * own, then how its comanda is split, with the split preview.
   */
  const editor = useProductEditorContext();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const form = computed(() => editor.form.value);
  const readOnly = computed(() => editor.readOnly.value);
  const categoryStations = computed(
    () => editor.category.value?.comandaPrinters ?? []
  );
  const inherits = computed(() => form.value?.comandaPrinters === null);
  const inheritLabel = computed(() => {
    const names = stationList(categoryStations.value, (id) =>
      restaurantStore.stationName(id)
    );
    return names
      ? t('menuPage.printing.inherit', { stations: names })
      : t('menuPage.printing.inheritNone');
  });

  const apart = computed(() =>
    form.value ? printsApart(form.value.comandaSplit) : false
  );
  const group = computed(() =>
    form.value ? groupsIdentical(form.value.comandaSplit) : false
  );
  const printsNowhere = computed(() => {
    const printers = form.value?.comandaPrinters;
    return printers === null
      ? !categoryStations.value.length
      : !(printers ?? []).length;
  });

  /** Turning it off starts from the category's stations: nothing changes yet. */
  function setInherits(value: boolean) {
    editor.update(
      'comandaPrinters',
      value ? null : [...categoryStations.value]
    );
  }

  function setApart(value: boolean) {
    editor.update('comandaSplit', splitFromSwitches(value, group.value));
  }

  function setGroup(value: boolean) {
    editor.update('comandaSplit', splitFromSwitches(apart.value, value));
  }
</script>

<template>
  <div v-if="form" class="v2-catalog-printing-settings">
    <PosUiSwitch
      class="printing-switch"
      :value="inherits"
      :label="inheritLabel"
      :disabled="readOnly"
      @input="setInherits"
    />
    <V2CatalogStationChips
      v-if="form.comandaPrinters !== null"
      class="printing-stations"
      :value="form.comandaPrinters"
      :label="t('menuPage.printing.stations')"
      :empty-warning="t('menuPage.printing.none')"
      :disabled="readOnly"
      @input="editor.update('comandaPrinters', $event)"
    />

    <div class="printing-split">
      <PosUiSwitch
        :value="apart"
        :label="t('item.comandaSeparate')"
        :disabled="readOnly"
        @input="setApart"
      />
      <PosUiSwitch
        v-if="apart"
        class="split-group"
        :value="group"
        :label="t('item.comandaGroupIdentical')"
        :disabled="readOnly"
        @input="setGroup"
      />
      <V2ItemComandaSplitPreview
        class="split-preview"
        :mode="form.comandaSplit"
        :item-name="form.name"
        :muted="printsNowhere"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-printing-settings {
    .printing-switch {
      @apply tw-w-full;
    }

    .printing-stations {
      @apply tw-mt-2;
    }

    .printing-split {
      @apply tw-mt-3 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-2;

      .split-group {
        @apply tw-pl-6;
      }

      .split-preview {
        @apply tw-mt-3;
      }
    }
  }
</style>
